// NOTE: Redux Toolkit allows us to assign values directly to the state in the reducers.
/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ScriptStudioBuffersState {
  byId: Record<
    string,
    {
      textDraft?: string;
      formattedTextDraft?: string;
      avatarDraft?: number;
      avatarVariantDraft?: number;
      avatarVariantVersionDraft?: string;
      lastEditedAt: number;
      /**
       * When the text drafts were last set. `lastEditedAt` also moves on voice
       * changes, so it can't tell whether the text is newer than the server's.
       */
      textEditedAt?: number;
      projectId: string;
    }
  >;
}

const initialState: ScriptStudioBuffersState = {
  byId: {},
};

const scriptStudioBuffersSlice = createSlice({
  name: 'scriptStudioBuffers',
  initialState,
  reducers: {
    hydrate(_state, action: PayloadAction<ScriptStudioBuffersState>) {
      return action.payload;
    },
    setTextDraft(
      state,
      action: PayloadAction<{
        id: string;
        projectId: string;
        plainText: string;
        formattedText: string;
      }>
    ) {
      const { id, plainText, formattedText, projectId } = action.payload;
      state.byId[id] ??= { lastEditedAt: Date.now(), projectId };
      state.byId[id].textDraft = plainText;
      state.byId[id].formattedTextDraft = formattedText;
      state.byId[id].lastEditedAt = Date.now();
      state.byId[id].textEditedAt = Date.now();
    },
    setFormattedTextDraft(
      state,
      action: PayloadAction<{
        id: string;
        projectId: string;
        formattedText: string;
      }>
    ) {
      const { id, formattedText, projectId } = action.payload;
      state.byId[id] ??= { lastEditedAt: Date.now(), projectId };
      state.byId[id].formattedTextDraft = formattedText;
      state.byId[id].lastEditedAt = Date.now();
      state.byId[id].textEditedAt = Date.now();
    },
    setFormattedTextDrafts(
      state,
      action: PayloadAction<{
        projectId: string;
        sections: Array<{ id: string; formattedText: string }>;
        /** Not saved to `section.editor` (e.g. mid cue slider drag) */
        skipSave?: boolean;
      }>
    ) {
      const { projectId, sections } = action.payload;
      const lastEditedAt = Date.now();
      for (const { id, formattedText } of sections) {
        state.byId[id] ??= { lastEditedAt, projectId };
        state.byId[id].formattedTextDraft = formattedText;
        state.byId[id].lastEditedAt = lastEditedAt;
        state.byId[id].textEditedAt = lastEditedAt;
      }
    },
    setAvatarDraft(
      state,
      action: PayloadAction<{ id: string; projectId: string; avatar: number }>
    ) {
      const { id, projectId, avatar } = action.payload;
      state.byId[id] ??= { lastEditedAt: Date.now(), projectId };
      state.byId[id].avatarDraft = avatar;
      state.byId[id].lastEditedAt = Date.now();
    },
    setAvatarVariantDraft(
      state,
      action: PayloadAction<{
        id: string;
        projectId: string;
        avatarVariant: number;
        avatarVariantVersion?: string;
      }>
    ) {
      const { id, projectId, avatarVariant, avatarVariantVersion } =
        action.payload;
      state.byId[id] ??= { lastEditedAt: Date.now(), projectId };
      state.byId[id].avatarVariantDraft = avatarVariant;
      // note: this will be undefined for script-first projects as we only render caruso
      state.byId[id].avatarVariantVersionDraft = avatarVariantVersion;
      state.byId[id].lastEditedAt = Date.now();
    },
    setAvatarDrafts(
      state,
      action: PayloadAction<{
        projectId: string;
        sections: Array<{
          id: string;
          avatar: number;
          avatarVariant: number;
          avatarVariantVersion?: string;
        }>;
      }>
    ) {
      const { projectId, sections } = action.payload;
      const lastEditedAt = Date.now();
      for (const section of sections) {
        state.byId[section.id] ??= { lastEditedAt, projectId };
        state.byId[section.id].avatarDraft = section.avatar;
        state.byId[section.id].avatarVariantDraft = section.avatarVariant;
        state.byId[section.id].avatarVariantVersionDraft =
          section.avatarVariantVersion;
        state.byId[section.id].lastEditedAt = lastEditedAt;
      }
    },
    setSectionDrafts(
      state,
      action: PayloadAction<{
        projectId: string;
        sections: Array<{
          id: string;
          plainText: string;
          formattedText: string;
          avatarVariant?: number;
          avatarVariantVersion?: string;
        }>;
      }>
    ) {
      const { projectId, sections } = action.payload;
      const lastEditedAt = Date.now();
      for (const section of sections) {
        state.byId[section.id] ??= { lastEditedAt, projectId };
        state.byId[section.id].textDraft = section.plainText;
        state.byId[section.id].formattedTextDraft = section.formattedText;
        if (section.avatarVariant !== undefined) {
          state.byId[section.id].avatarVariantDraft = section.avatarVariant;
          state.byId[section.id].avatarVariantVersionDraft =
            section.avatarVariantVersion;
        }
        state.byId[section.id].lastEditedAt = lastEditedAt;
        state.byId[section.id].textEditedAt = lastEditedAt;
      }
    },
    setProjectDrafts(
      state,
      action: PayloadAction<{
        projectId: string;
        sections: Array<{
          id: string;
          plainText: string;
          formattedText: string;
          avatarId: number;
          avatarVariantId: number;
          avatarVariantVersion?: string;
        }>;
        /** Not saved to `section.editor` (e.g. restoring a bulk run's items) */
        skipSave?: boolean;
      }>
    ) {
      const { projectId, sections } = action.payload;
      for (const section of sections) {
        state.byId[section.id] ??= { lastEditedAt: Date.now(), projectId };
        state.byId[section.id].textDraft = section.plainText;
        state.byId[section.id].formattedTextDraft = section.formattedText;
        state.byId[section.id].avatarDraft = section.avatarId;
        state.byId[section.id].avatarVariantDraft = section.avatarVariantId;
        state.byId[section.id].avatarVariantVersionDraft =
          section.avatarVariantVersion;
        state.byId[section.id].lastEditedAt = Date.now();
        state.byId[section.id].textEditedAt = Date.now();
      }
    },
    clearProjectDrafts(state, action: PayloadAction<string>) {
      Object.entries(state.byId).forEach(([id, value]) => {
        if (value.projectId === action.payload) {
          delete state.byId[id];
        }
      });
    },
    clearDraft(state, action: PayloadAction<string>) {
      delete state.byId[action.payload];
    },
    /**
     * Drops a buffer that a newer take has outdated. The views already ignore
     * it, but touching `lastEditedAt` would make it current again.
     */
    clearOutdatedDraft(
      state,
      action: PayloadAction<{ id: string; takeCreatedAt: string }>
    ) {
      const { id, takeCreatedAt } = action.payload;
      const buffer = state.byId[id];
      if (buffer && buffer.lastEditedAt < Number(takeCreatedAt)) {
        delete state.byId[id];
      }
    },
    clearDrafts(state, action: PayloadAction<string[]>) {
      for (const id of action.payload) {
        delete state.byId[id];
      }
    },
    clearVoiceDrafts(state, action: PayloadAction<string[]>) {
      for (const id of action.payload) {
        const buffer = state.byId[id];
        if (buffer) {
          // Clear only voice drafts, preserve unsaved text
          delete buffer.avatarDraft;
          delete buffer.avatarVariantDraft;
          delete buffer.avatarVariantVersionDraft;
          if (!buffer.textDraft && !buffer.formattedTextDraft) {
            delete state.byId[id];
          }
        }
      }
    },
    clearTextDraft(state, action: PayloadAction<string>) {
      const buffer = state.byId[action.payload];
      if (buffer) {
        // Clear only text-related drafts, preserve avatar drafts
        delete buffer.textDraft;
        delete buffer.formattedTextDraft;
        delete buffer.textEditedAt;
        // If no drafts remain, delete the entire buffer entry
        if (
          !buffer.avatarDraft &&
          !buffer.avatarVariantDraft &&
          !buffer.avatarVariantVersionDraft
        ) {
          delete state.byId[action.payload];
        }
      }
    },
  },
});

export const {
  hydrate,
  setTextDraft,
  setFormattedTextDraft,
  setFormattedTextDrafts,
  setAvatarDraft,
  setAvatarVariantDraft,
  setAvatarDrafts,
  setSectionDrafts,
  setProjectDrafts,
  clearProjectDrafts,
  clearDraft,
  clearDrafts,
  clearOutdatedDraft,
  clearTextDraft,
  clearVoiceDrafts,
} = scriptStudioBuffersSlice.actions;

export default scriptStudioBuffersSlice;
